import { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import useOutside from "../../hooks/useOutside";
import "./BreadcrumbParentsMenu.css";

/**
 * BreadcrumbParentsMenu Component
 * 
 * Displays a dropdown menu showing hidden parent items from a truncated breadcrumb.
 * Similar to ContextMenuComponent but specifically designed for breadcrumb navigation.
 * Uses a React Portal to render at document root level to avoid z-index stacking issues.
 * 
 * @param {Object} props
 * @param {Array} props.hiddenParents - Array of parent items that are hidden in the breadcrumb
 * @param {Function} props.onParentClick - Callback function when a parent is clicked (receives parent.id)
 * @param {Function} props.onClose - Callback function to close the menu
 * @param {Object} props.ellipsisButtonRef - Ref to the ellipsis button that triggered the menu
 */
const BreadcrumbParentsMenu = (props) => {
    const { hiddenParents, onParentClick, onClose, ellipsisButtonRef } = props;
    const menuRef = useRef();
    const [position, setPosition] = useState({ top: 0, left: 0 });

    // Calculate menu position based on ellipsis button location
    useEffect(() => {
        const updatePosition = () => {
            if (ellipsisButtonRef.current) {
                const buttonRect = ellipsisButtonRef.current.getBoundingClientRect();
                const viewportHeight = window.innerHeight;
                const viewportWidth = window.innerWidth;
                
                // Estimate menu height (max 300px from CSS)
                const estimatedMenuHeight = Math.min(300, hiddenParents.length * 40);
                
                // Check if menu would overflow bottom of viewport
                let top = buttonRect.bottom + 4;
                if (top + estimatedMenuHeight > viewportHeight - 16) {
                    // Position above button instead
                    top = buttonRect.top - estimatedMenuHeight - 4;
                }
                
                // Check if menu would overflow right of viewport
                let left = buttonRect.left;
                const menuWidth = 320; // max-width from CSS
                if (left + menuWidth > viewportWidth - 16) {
                    left = viewportWidth - menuWidth - 16;
                }
                
                setPosition({ top, left });
            }
        };

        updatePosition();

        // Update position on scroll and resize
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [ellipsisButtonRef, hiddenParents.length]);

    // Close menu when clicking outside
    useOutside(menuRef, ellipsisButtonRef, null, () => onClose());

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const handleParentItemClick = (parentId) => {
        onParentClick(parentId);
        onClose();
    };

    // Render menu using portal at document root to avoid z-index issues
    const menuContent = (
        <div 
            className="gt-breadcrumb-parents-menu-wrapper"
            style={{
                position: 'fixed',
                top: `${position.top}px`,
                left: `${position.left}px`
            }}
        >
            <ul
                className="gt-context-menu gt-breadcrumb-parents-menu"
                role="menu"
                aria-orientation="vertical"
                aria-label="Hidden parent items"
                ref={menuRef}
            >
                {hiddenParents.map((parent, index) => (
                    <li 
                        key={parent.id || index} 
                        className="gt-context-menu__item" 
                        role="menuitem"
                    >
                        <button
                            onClick={() => handleParentItemClick(parent.id)}
                            title={parent.title}
                        >
                            <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                viewBox="0 0 16 16"
                            >
                                <use xlinkHref="#globaltasks-svg-folder" />
                            </svg>
                            {parent.title}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );

    return createPortal(menuContent, document.body);
};

export default BreadcrumbParentsMenu;
